import * as React from 'react';
import moment from 'moment';
import { parse } from 'query-string';
import {
  PageHelpDefinition,
  PageHelpRegistration,
  registerPageHelpDefinition
} from 'Src/helpers/pageHelp/pageHelp';

interface Props {
  location: any;
  history: any;
}

interface RouteGuide {
  id: string;
  title: string;
  subtitle: string;
  area: string;
  items: string[];
  actionHint: string;
}

const ROUTE_GUIDES: Array<{ matches: (pathname: string) => boolean; guide: RouteGuide }> = [
  {
    matches: path => path === '/tournaments/tournamentView',
    guide: {
      id: 'form',
      title: 'Tournament details',
      subtitle: 'Create or edit the core tournament record before managing entries and competition.',
      area: 'Tournament form',
      items: [
        'Set the sport, format, audience, dates, venue and entry window carefully; these values affect later availability and communication.',
        'Save the record before moving into entrants, teams and competition setup.',
        'Editing a completed tournament is supported when corrections are needed after the event.'
      ],
      actionHint: 'Use the form actions to save or cancel. Unsaved changes should be reviewed before leaving.'
    }
  },
  {
    matches: path => path === '/tournaments/notifications/send',
    guide: {
      id: 'notification-compose',
      title: 'Send tournament notification',
      subtitle: 'Prepare a targeted tournament update and preview it before sending.',
      area: 'Notification composer',
      items: [
        'Check the affected schools and channels before sending.',
        'Use the preview to verify the message as recipients will see it.',
        'The resulting individual deliveries can be reviewed in Notification history.'
      ],
      actionHint: 'Sending creates real notifications, so Squady explains the action but does not submit it for you.'
    }
  },
  {
    matches: path => path === '/tournaments/notifications',
    guide: {
      id: 'notifications',
      title: 'Tournament notification history',
      subtitle: 'Review what was sent, to whom and whether each delivery succeeded.',
      area: 'Notification history',
      items: [
        'Use filters to narrow by date, recipient or delivery state.',
        'Open a notification when you need its full delivery details.',
        'History remains useful after the tournament for investigating or confirming communication.'
      ],
      actionHint: 'Use the back link to return to the same tournament management context.'
    }
  },
  {
    matches: path => path === '/tournaments/manage/entries/eligible/notification',
    guide: {
      id: 'school-notification',
      title: 'Notify eligible schools',
      subtitle: 'Send a tournament update to the schools staged in the eligible-school list.',
      area: 'School notification',
      items: [
        'The recipient schools come from the selection made on Eligible schools.',
        'Review the recipient count and content before sending.',
        'Return without sending if the staged school selection is wrong.'
      ],
      actionHint: 'Sending is intentionally left as an explicit user action.'
    }
  },
  {
    matches: path => path === '/tournaments/participants' || path === '/tournaments/confirmationRequests/send',
    guide: {
      id: 'participants',
      title: 'Tournament participants',
      subtitle: 'Review participating schools and prepare confirmation requests.',
      area: 'Participants',
      items: [
        'Participant schools have accepted or been placed into the tournament.',
        'Confirmation requests ask booked schools to reconfirm and can collect additional information.',
        'Completed tournaments remain editable when the final participant record needs correction.'
      ],
      actionHint: 'Selection actions affect the checked schools; row opening and checkbox selection are different.'
    }
  },
  {
    matches: path => path === '/tournaments/eligibleSchools' || path === '/tournaments/athleteSchools',
    guide: {
      id: 'eligible-schools',
      title: 'Eligible schools',
      subtitle: 'Choose which schools may enter and manage their invitation state.',
      area: 'Eligible schools',
      items: [
        'Use filters and search to find schools before selecting them.',
        'Invitation actions apply to the selected schools, not merely the visible page.',
        'Eligibility can still be corrected for a past tournament when the historical record is wrong.'
      ],
      actionHint: 'Removal and bulk invitation changes remain confirmed actions.'
    }
  },
  {
    matches: path => path === '/tournaments/confirmationRequests',
    guide: {
      id: 'confirmation-requests',
      title: 'Tournament confirmation requests',
      subtitle: 'Track final replies from schools already connected to the tournament.',
      area: 'Confirmation requests',
      items: [
        'Pending means the school has not completed the request yet.',
        'Rejected responses should be checked against the final participant and fixture lists.',
        'The response history remains available after the tournament.'
      ],
      actionHint: 'Open a row for its form and response details; use filters for status or school.'
    }
  },
  {
    matches: path => path === '/tournaments/waitingList',
    guide: {
      id: 'waiting-list',
      title: 'Tournament waiting list',
      subtitle: 'Manage queued schools when tournament capacity is limited.',
      area: 'Waiting list',
      items: [
        'Queue order and capacity should be reviewed before moving a school into participants.',
        'Moving or removing a school changes its tournament place and should be confirmed.',
        'Past waiting-list corrections remain possible when the recorded history is inaccurate.'
      ],
      actionHint: 'Squady will not move or remove a school automatically.'
    }
  },
  {
    matches: path => path === '/tournaments/teams/members',
    guide: {
      id: 'team-members',
      title: 'Tournament team members',
      subtitle: 'Manage the children or athletes entered in a selected tournament team.',
      area: 'Team members',
      items: [
        'Check age, gender and event eligibility before adding a participant.',
        'For individual tournaments, the team can act as the container for participants before event allocation.',
        'Members can be corrected after the tournament, including when final results expose an entry mistake.'
      ],
      actionHint: 'Save membership changes before returning to the team list.'
    }
  },
  {
    matches: path => path === '/tournaments/teams',
    guide: {
      id: 'teams',
      title: 'Tournament teams',
      subtitle: 'Create school entries, assign tournament numbers and open team membership.',
      area: 'Teams',
      items: [
        'A team links a participating school to the tournament competition.',
        'Open a team to manage its members; use tournament numbers consistently in start lists and results.',
        'Past tournament teams and membership remain editable for operational corrections.'
      ],
      actionHint: 'Use row actions for one team and selection actions only when a bulk operation is intended.'
    }
  },
  {
    matches: path => path === '/tournaments/grid',
    guide: {
      id: 'grid',
      title: 'Tournament groups and draw',
      subtitle: 'Arrange teams into competition groups and build the tournament structure.',
      area: 'Competition grid',
      items: [
        'Check team distribution before creating the next competition stage.',
        'Group changes can affect fixtures and later results.',
        'The draw remains editable after the tournament when correcting the official record.'
      ],
      actionHint: 'Review downstream fixtures and results after changing a group or draw.'
    }
  },
  {
    matches: path => path === '/tournaments/events/event',
    guide: {
      id: 'event',
      title: 'Tournament event',
      subtitle: 'Manage one fixture or individual event, its participants and result.',
      area: 'Tournament event',
      items: [
        'Review participant schools or athletes before recording the result.',
        'Scores, times, distances, points and placings depend on the tournament format.',
        'Completed events remain editable so result errors can be corrected and republished.'
      ],
      actionHint: 'Save result changes and recheck any standings or public output that depends on them.'
    }
  },
  {
    matches: path => path === '/tournaments/events',
    guide: {
      id: 'events',
      title: 'Tournament events',
      subtitle: 'Create and manage the tournament programme, allocations and results.',
      area: 'Events and fixtures',
      items: [
        'Team tournaments use fixtures; individual tournaments use events and participant allocations.',
        'Open an event to manage participants, settings and its result.',
        'Past fixtures and events stay editable for scores, points, placings and other corrections.'
      ],
      actionHint: 'Changing events may affect groups, standings, reports and public results.'
    }
  },
  {
    matches: path => path === '/tournaments/pitches',
    guide: {
      id: 'pitches',
      title: 'Tournament pitches',
      subtitle: 'Create competition areas and assign fixtures to the correct place.',
      area: 'Pitches',
      items: [
        'Use clear pitch names that staff and visitors can recognise.',
        'Check fixture assignments after editing or removing a pitch.',
        'Pitch assignments remain correctable after the tournament.'
      ],
      actionHint: 'Review affected fixtures before removing a pitch.'
    }
  },
  {
    matches: path => path === '/tournaments/customGroups',
    guide: {
      id: 'groups',
      title: 'Tournament groups',
      subtitle: 'Define group structure and the teams placed into each group.',
      area: 'Groups',
      items: [
        'Group membership drives fixtures and progression in team competitions.',
        'Changing groups can require fixture or result review.',
        'Past groups remain editable when correcting the completed competition.'
      ],
      actionHint: 'Check the competition grid after group changes.'
    }
  },
  {
    matches: path => path === '/tournaments/admins',
    guide: {
      id: 'admins',
      title: 'Tournament admins',
      subtitle: 'Control which union users can administer this tournament.',
      area: 'Tournament admins',
      items: [
        'Tournament admins receive access only within the permissions supported by their union role.',
        'Remove access when it is no longer needed.',
        'Admin access can be corrected for completed tournaments too.'
      ],
      actionHint: 'Access changes take effect for the selected user and should be reviewed carefully.'
    }
  },
  {
    matches: path => path === '/tournaments/logs',
    guide: {
      id: 'logs',
      title: 'Tournament enrollment log',
      subtitle: 'Review the audit trail for tournament entry and waiting-list changes.',
      area: 'Enrollment log',
      items: [
        'Use the log to understand when and why a school changed entry state.',
        'Comments add operational context without rewriting the original history.',
        'Past tournament logs remain available during corrections and support work.'
      ],
      actionHint: 'Use filters to narrow by school, action or date.'
    }
  },
  {
    matches: path => path.indexOf('/tournaments/invites/') === 0,
    guide: {
      id: 'invites',
      title: 'Tournament invites',
      subtitle: 'Review invitations sent to schools and their current response state.',
      area: 'Invites',
      items: [
        'Outbox contains active invitation work; Archive contains resolved or historical invitations.',
        'Open a row for its tournament and response details.',
        'Use filters to avoid loading or scanning unrelated historical records.'
      ],
      actionHint: 'Invitation changes affect school access to the tournament and remain explicit actions.'
    }
  }
];

const defaultGuide: RouteGuide = {
  id: 'tournaments',
  title: 'Union tournaments',
  subtitle: 'Manage tournament entry, competition and publishing work.',
  area: 'Tournament workspace',
  items: [
    'Use the tournament management hub to move between entries, teams, events, results, publishing and settings.',
    'Completed tournaments remain operationally editable when participants or results need correction.',
    'Squady never performs destructive tournament actions automatically.'
  ],
  actionHint: 'Use the visible page actions to continue the current workflow.'
};

const getTournamentId = (location: any): string => {
  const direct = parse(location.search || '');
  const previous = parse((location.state && location.state.search) || '');
  const value = direct.tournament || direct.tournamentId || previous.tournament || previous.tournamentId;
  return Array.isArray(value) ? value[0] : value || '';
};

export const UnionTournamentRoutePageHelp: React.FunctionComponent<Props> = props => {
  const registrationRef = React.useRef<PageHelpRegistration | null>(null);
  const location = props.location || props.history.location || window.location;
  const pathname = location.pathname || window.location.pathname;
  const guideEntry = ROUTE_GUIDES.find(item => item.matches(pathname));
  const guide = guideEntry ? guideEntry.guide : defaultGuide;
  const tournament = location.state && location.state.tournament;
  const isPast = Boolean(
    tournament && moment(tournament.endTime || tournament.startTime).isBefore(moment())
  );
  const tournamentId = getTournamentId(location);

  const definition: PageHelpDefinition = {
    screenId: `union.tournaments.${guide.id}`,
    title: guide.title,
    subtitle: guide.subtitle,
    entity: { singular: 'tournament record', plural: 'tournament records' },
    state: { activeView: guide.area },
    observations: [
      ...(isPast
        ? [
            {
              id: 'past-route-editable',
              tone: 'positive' as 'positive',
              title: 'Past tournament · editing remains available',
              text:
                'This tournament is completed, but the current management area remains active for participant, team, event, allocation, score, placing and result corrections.'
            }
          ]
        : [])
    ],
    actions: [
      ...(tournamentId && pathname.indexOf('/tournaments/manage') !== 0
        ? [
            {
              id: 'union-tournament-back-to-management',
              label: 'Back to tournament management',
              description: 'Return to the tournament overview without discarding the tournament context.',
              kind: 'primary' as 'primary',
              anchorId: 'union-tournament-route-header',
              run: () =>
                props.history.push({
                  pathname: '/tournaments/manage',
                  search: `tournament=${tournamentId}`,
                  state: location.state
                })
            }
          ]
        : []),
      {
        id: 'union-tournament-route-back',
        label: 'Go back',
        description: 'Return to the previous tournament screen.',
        run: () => props.history.goBack()
      }
    ],
    anchors: [
      {
        id: 'union-tournament-route-header',
        label: guide.title,
        description: guide.subtitle,
        selector:
          '[data-help-anchor="union-tournament-header"], [data-help-anchor="union-tournaments-header"], .ePageHeader, .unionTournamentsHeader, header'
      },
      {
        id: 'union-tournament-route-content',
        label: guide.area,
        description: guide.actionHint,
        selector:
          '[data-help-anchor="union-tournament-content"], [data-help-anchor="union-tournaments-list"], .eGrid2, .table, main'
      }
    ],
    sections: [
      {
        id: 'current-workflow',
        title: `How ${guide.area} works`,
        items: guide.items,
        anchorIds: ['union-tournament-route-content']
      },
      {
        id: 'available-actions',
        title: 'Actions on this screen',
        items: [
          guide.actionHint,
          'Opening a row and selecting its checkbox are different actions wherever both controls are shown.',
          'Squady can navigate to safe management areas but leaves deletion, removal, sending and bulk state changes for you to confirm.'
        ],
        anchorIds: ['union-tournament-route-header', 'union-tournament-route-content']
      },
      {
        id: 'past-tournament-policy',
        title: 'Completed tournaments stay editable',
        items: [
          'Do not treat the Past list as a read-only archive.',
          'Union admins may need to correct participants, teams, event allocations, fixtures, scores, points, placings and results after the date has passed.',
          'Publishing and settings remain available so corrected results and details can be shown accurately.'
        ],
        anchorIds: ['union-tournament-route-content']
      }
    ]
  };

  React.useEffect(() => {
    registrationRef.current = registerPageHelpDefinition(definition);
    return () => {
      if (registrationRef.current) registrationRef.current.unregister();
      registrationRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (registrationRef.current) registrationRef.current.update(definition);
  }, [pathname, location.search, guide.id, isPast, tournamentId]);

  return null;
};
